import { AdaptiveWand } from '@music-vine/cadence/icons';
import { Button, Text } from '@music-vine/cadence/ui';
import Image from 'next/image';
import type { PageDialogProps } from '@/components/PageDialog/PageDialog';
const dismissDialogue = () => {
  window.postMessage({
    type: 'DISMISS_DIALOGUE'
  }, window.location.origin);
};
const openMobileDrawer = () => {
  window.postMessage({
    type: 'OPEN_DIALOGUE_DRAWER'
  }, window.location.origin);
};

// Allow-list: music browse pages (home, all tracks, categories, collections,
// tag pages, premium spotlight), latest releases, and trending.
const showOnSlugs = [/^\/music(?:\/|$)/, /^\/trending(?:\/|$)/];

// Carve the customize workspace itself out of the /music allow-list.
const customizeRegex = /^\/music\/customize(?:\/|$)/;

/**
 * "Introducing Adaptive Music" announcement, surfaced on music browse pages and
 * trending through the `PageDialogQueue` (see `showOnSlugs`). Promotes the
 * customize workspace where users can fit any track to an exact length. Returns
 * `null` when the feature flag is off so the queue skips it entirely.
 */
export const getAdaptiveMusicIntroProps = ({
  isEnabled
}: {
  isEnabled: boolean;
}): PageDialogProps | null => {
  if (!isEnabled) {
    return null;
  }
  return {
    storageKey: 'adaptive-music-announcement',
    delay: 6000,
    title: 'Track duration customization',
    image: {
      src: 'https://cdn.uppbeat.io/images/adaptive-music-launch-graphic.png',
      alt: 'Adaptive Music',
      badge: 'NEW FEATURE'
    },
    includeSlugs: showOnSlugs,
    excludeSlugs: [customizeRegex],
    // Retired from the queue; still previewable from the admin bar.
    isHidden: true,
    mobileChildren: <div className="flex items-center justify-between gap-0 px-2 py-3">
        <div className="flex items-center gap-2">
          <div className="flex rounded-full bg-brand-secondary p-2.5 dark:bg-pink-950">
            <AdaptiveWand className="size-4 text-pink-600 [&_path]:stroke-[1.1px]" />
          </div>
          <p className="text-base text-gray-950 dark:text-white">
            <span className="font-semibold">NEW</span>: Customize duration
          </p>
        </div>
        <button className="flex shrink-0 items-center gap-0.5 font-semibold text-base text-pink-600" onClick={openMobileDrawer} type="button">
          Learn More
        </button>
      </div>,
    drawerChildren: <>
        <div className="flex flex-col gap-6 dark:bg-gray-875">
          <div className="flex flex-col gap-1 text-center">
            <p className="text-brand-primary-hover text-sm">NEW FEATURE</p>
            <h2 className="font-semibold text-lg">
              Track duration customization
            </h2>
            <Text className="text-pretty text-gray-700 text-sm dark:text-gray-50">
              No more awkward cuts or jumping between apps. Just pick a duration
              and have any artist-made track customized for your content.
            </Text>
          </div>

          <Image alt="Adaptive Music in action" className="mx-auto hidden h-auto w-full max-w-90 dark:block" height={200} src="https://cdn.uppbeat.io/images/adaptive-music-customize-menu-drawer-dark.png" width={360} />

          <Image alt="Adaptive Music in action" className="mx-auto h-auto w-full max-w-90 dark:hidden" height={200} src="https://cdn.uppbeat.io/images/adaptive-music-customize-menu-drawer-light.png" width={360} />

          <Button className="w-full" fontSize="sm" onClick={dismissDialogue} size="lg" type="button" variant="brand">
            Explore Uppbeat
          </Button>
        </div>
      </>,
    children:
    // CTA just closes the dialog — no navigation.
    <>
        <div className="flex flex-col gap-6 p-8 dark:bg-gray-875">
          <div className="flex flex-col gap-1">
            <h2 className="text-center font-semibold text-2xl">
              Track duration customization
            </h2>
            <Text className="text-pretty text-center text-gray-700 text-sm dark:text-gray-50">
              No more awkward cuts or jumping between apps. Just pick a duration
              and have any artist-made track customized for your content.
            </Text>
          </div>

          <Image alt="Adaptive Music in action" className="mx-auto h-auto w-full max-w-90 dark:hidden" height={200} src="https://cdn.uppbeat.io/images/adaptive-music-customize-menu-light.png" width={360} />

          <Image alt="Adaptive Music in action" className="mx-auto hidden h-auto w-full max-w-90 dark:block" height={200} src="https://cdn.uppbeat.io/images/adaptive-music-customize-menu-dark.png" width={360} />

          <Button className="w-full" fontSize="sm" onClick={dismissDialogue} size="lg" type="button" variant="brand">
            Explore Uppbeat
          </Button>
        </div>
      </>
  };
};